import type { WhatsappConnectionReason, WhatsappStatus } from '../types/whatsapp.types';
import { daysSince } from './whatsapp-format';

/* ==========================================================================
   O que a faixa global do WhatsApp diz (change whatsapp-channel, D32)

   Um aviso por vez, do mais grave ao mais leve: a conexão caiu pela Meta, a
   Meta pede uma ação, as mensagens não saem, e o lembrete de abrir o app no
   celular principal. Só o lembrete se dispensa: os outros somem sozinhos
   quando o problema acaba. Cada aviso leva a `/settings/customer-agent`.
   ========================================================================== */

/** A Meta desconecta com 14 dias sem abrir o app; o painel lembra a partir do décimo. */
export const APP_REMINDER_AFTER_DAYS = 10;

/** O lembrete dispensado volta depois disto, naquele navegador. */
export const APP_REMINDER_DISMISS_MS = 24 * 60 * 60_000;

export type WhatsappNoticeKind = 'disconnected' | 'action_required' | 'outbound_failing' | 'app_reminder';

export interface WhatsappNotice {
  kind: WhatsappNoticeKind;
  text: string;
  linkLabel: string;
}

function disconnectedText(reason: WhatsappConnectionReason): string {
  switch (reason) {
    case 'app_inactive':
      return 'O WhatsApp da loja foi desconectado: o app WhatsApp Business ficou 14 dias sem ser aberto no celular principal.';
    case 'removed_in_app':
      return 'O WhatsApp da loja foi desconectado pelo app WhatsApp Business do celular.';
    case 'token_revoked':
      return 'O WhatsApp da loja foi desconectado: a permissão dada à Vapt Menu na Meta foi retirada ou venceu.';
    case 'plan_entitlement_lost':
    case 'by_platform':
      return 'O WhatsApp da loja foi desconectado depois de 30 dias sem o plano do Atendente IA.';
    default:
      return 'O WhatsApp da loja foi desconectado.';
  }
}

function actionRequiredText(reason: WhatsappConnectionReason): string {
  switch (reason) {
    case 'payment_method_missing':
      return 'A Meta precisa de um cartão cadastrado na conta do WhatsApp da loja para enviar as mensagens.';
    case 'meta_restricted':
      return 'A Meta restringiu o número da loja, e o envio de mensagens do WhatsApp está pausado.';
    case 'quality_low':
      return 'A qualidade do número da loja na Meta está baixa: clientes estão bloqueando ou denunciando as mensagens.';
    case 'other_automation_detected':
      return 'Outro robô ou resposta automática está respondendo no WhatsApp da loja. Desligue a outra automação.';
    case 'app_inactive_warning':
      return 'Abra o app WhatsApp Business no celular principal: a Meta avisou que vai desconectar o número por falta de uso.';
    default:
      return 'A conexão do WhatsApp da loja precisa de atenção.';
  }
}

/** A última atividade do app: o último eco, ou a própria conexão quando ainda não houve eco. */
export function lastAppActivity(status: WhatsappStatus): string | null {
  return status.connection.lastAppActivityAt ?? status.connection.connectedAt;
}

/**
 * O aviso da faixa, ou `null`. `reminderDismissed` diz se o lembrete dos 14
 * dias foi dispensado nas últimas 24 h.
 */
export function whatsappNotice(
  status: WhatsappStatus,
  { now = Date.now(), reminderDismissed = false }: { now?: number; reminderDismissed?: boolean } = {},
): WhatsappNotice | null {
  const { state, reason } = status.connection;

  if (state === 'disconnected') {
    // A desconexão pelo próprio painel não é problema a avisar.
    if (reason === 'by_owner') return null;
    return { kind: 'disconnected', text: disconnectedText(reason), linkLabel: 'Reconectar' };
  }

  if (state === 'action_required') {
    return { kind: 'action_required', text: actionRequiredText(reason), linkLabel: 'Resolver' };
  }

  if (status.outboundHealth.failing && (state === 'connected' || state === 'syncing')) {
    return {
      kind: 'outbound_failing',
      text: 'As mensagens do WhatsApp da loja não estão saindo.',
      linkLabel: 'Ver a conexão',
    };
  }

  if (state === 'connected' && !reminderDismissed) {
    const activity = lastAppActivity(status);
    if (activity) {
      const days = daysSince(activity, now);
      if (days >= APP_REMINDER_AFTER_DAYS) {
        return {
          kind: 'app_reminder',
          text:
            `Faz ${days} dias que o app WhatsApp Business não é aberto no celular principal. ` +
            'Abra o app: com 14 dias sem uso, a Meta desconecta o número.',
          linkLabel: 'Ver a conexão',
        };
      }
    }
  }

  return null;
}

/* ---- Dispensa do lembrete, por loja e por navegador ---- */

export function appReminderDismissKey(tenantId: string): string {
  return `vaptmenu.panel.whatsapp-app-reminder-dismissed.${tenantId}`;
}

/**
 * O lembrete foi dispensado há menos de 24 h? O armazenamento pode estar
 * indisponível (modo privado, política do navegador): o pior caso aceitável é
 * o lembrete reaparecer, nunca uma tela quebrada.
 */
export function readAppReminderDismissal(tenantId: string, now: number = Date.now()): boolean {
  if (!tenantId) return false;
  try {
    const value = Number(localStorage.getItem(appReminderDismissKey(tenantId)));
    return Number.isFinite(value) && value > 0 && now - value < APP_REMINDER_DISMISS_MS;
  } catch {
    return false;
  }
}

/**
 * O lembrete está dispensado agora? Vale a dispensa desta sessão (`sessionDismissedAt`,
 * guardada em memória pela faixa) ou a do navegador.
 */
export function isAppReminderDismissed(
  tenantId: string,
  sessionDismissedAt: number | undefined,
  now: number = Date.now(),
): boolean {
  return (
    (sessionDismissedAt !== undefined && now - sessionDismissedAt < APP_REMINDER_DISMISS_MS) ||
    readAppReminderDismissal(tenantId, now)
  );
}

export function writeAppReminderDismissal(tenantId: string, now: number = Date.now()): void {
  if (!tenantId) return;
  try {
    localStorage.setItem(appReminderDismissKey(tenantId), String(now));
  } catch {
    // A dispensa vale só nesta sessão, pela memória do componente.
  }
}
